import React, { useState, useEffect, useMemo } from 'react';
import { computeFigDifficulty } from '../utils/figDifficulty';
import { normalizeFig } from '../utils/skillSearch';
import { computeDmtDifficulty, explainDmtDifficulty } from '../utils/dmtDifficulty';
import '../pages/booking/booking-shared.css';
import './SkillFormModal.css';

// Encode/decode the halfTwistsPerSom array to the FIG digit string convention.
//   [0, 1]  ⇄ '-1'
//   [0, 0]  ⇄ '--'
//   [4]     ⇄ '4'
//   []      ⇄ ''
export function encodeHalfTwists(arr) {
  if (!arr || arr.length === 0) return '';
  return arr.map(t => (t === 0 || t == null) ? '-' : String(t)).join('');
}
export function decodeHalfTwists(str) {
  if (!str) return [];
  return str.split('').map(c => (c === '-' ? 0 : Number(c) || 0));
}

// Unified Add/Edit Skill modal. Structured fields drive the FIG calculator;
// difficulty / FIG / suggested name auto-populate but stay editable for overrides.
// Set showOrder=false when called outside a level context (library skills).
export default function SkillFormModal({ mode, skill = null, onSave, onCancel, showOrder = true, extraActions = null }) {
  const isEdit = mode === 'edit';
  const [name, setName] = useState(skill?.name ?? '');
  const [description, setDescription] = useState(skill?.description ?? '');
  const [order, setOrder] = useState(skill?.order ?? 1);

  // Structured params
  const [quarterSoms, setQuarterSoms] = useState(skill?.quarterSoms ?? 0);
  const [twistsArr, setTwistsArr] = useState(decodeHalfTwists(skill?.halfTwistsPerSom ?? ''));
  const [shape, setShape] = useState(skill?.shape ?? '');
  const [prepSkill, setPrepSkill] = useState(Boolean(skill?.prepSkill));
  const [startPosition, setStartPosition] = useState(skill ? (skill.startPosition ?? '') : 'feet');
  const [landing, setLanding] = useState(skill?.landing ?? 'feet');
  const [direction, setDirection] = useState(skill?.direction ?? 'backward');

  // Derived (editable)
  const [difficulty, setDifficulty] = useState(skill?.difficulty != null ? String(skill.difficulty) : '');
  const [figNotation, setFigNotation] = useState(skill?.figNotation ?? '');
  // On edit, a saved value that differs from the calculation was typed by hand.
  const savedCalc = useMemo(() => (isEdit && skill ? computeFigDifficulty({
    quarterSoms: skill.quarterSoms ?? 0,
    halfTwistsPerSom: decodeHalfTwists(skill.halfTwistsPerSom ?? ''),
    shape: skill.shape || null,
    landing: skill.landing ?? 'feet',
    direction: skill.direction ?? 'backward',
    prepSkill: Boolean(skill.prepSkill),
  }) : null), []); // eslint-disable-line react-hooks/exhaustive-deps
  const [diffOverridden, setDiffOverridden] = useState(isEdit && skill?.difficulty != null
    && Number(skill.difficulty) !== savedCalc.difficulty);
  const [dmtDifficulty, setDmtDifficulty] = useState(skill?.dmtDifficulty != null ? String(Number(skill.dmtDifficulty)) : '');
  // A saved DMT value that differs from the calculated one was typed by hand.
  const [dmtOverridden, setDmtOverridden] = useState(() => isEdit && skill?.dmtDifficulty != null
    && Number(skill.dmtDifficulty) !== computeDmtDifficulty(skill));
  const [figOverridden, setFigOverridden] = useState(isEdit && !!skill?.figNotation
    && normalizeFig(skill.figNotation) !== normalizeFig(savedCalc.figNotation));

  // Keep twistsArr length in sync with quarterSoms — but always show at least
  // one twist box so 0-som skills (½ Twist, Full Twist) can be entered.
  const expectedTwistEntries = Math.max(1, Math.ceil(quarterSoms / 4));
  useEffect(() => {
    setTwistsArr(prev => {
      const next = prev.slice(0, expectedTwistEntries);
      while (next.length < expectedTwistEntries) next.push(0);
      return next;
    });
  }, [expectedTwistEntries]);

  const calc = useMemo(() => computeFigDifficulty({
    quarterSoms,
    halfTwistsPerSom: twistsArr,
    shape: shape || null,
    landing,
    direction,
    prepSkill,
  }), [quarterSoms, twistsArr, shape, landing, direction, prepSkill]);

  const calcDmt = useMemo(() => computeDmtDifficulty({
    name, quarterSoms, halfTwistsPerSom: twistsArr, shape: shape || null, prepSkill, startPosition: startPosition || null, landing,
  }), [name, quarterSoms, twistsArr, shape, prepSkill, startPosition, landing]);
  const dmtWorking = useMemo(() => explainDmtDifficulty({
    name, quarterSoms, halfTwistsPerSom: twistsArr, shape: shape || null, prepSkill, startPosition: startPosition || null, landing,
  }), [name, quarterSoms, twistsArr, shape, prepSkill, startPosition, landing]);

  useEffect(() => { if (!diffOverridden) setDifficulty(String(calc.difficulty)); }, [calc.difficulty, diffOverridden]);
  useEffect(() => {
    if (!dmtOverridden) setDmtDifficulty(calcDmt == null ? '' : String(calcDmt));
  }, [calcDmt, dmtOverridden]);
  useEffect(() => { if (!figOverridden) setFigNotation(calc.figNotation); }, [calc.figNotation, figOverridden]);

  // Changing the shape recalculates every value that wasn't typed by hand.
  const structureChanged = () => {
    setDiffOverridden(false);
    setFigOverridden(false);
    setDmtOverridden(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = {
      name,
      description,
      quarterSoms: Number(quarterSoms) || 0,
      halfTwistsPerSom: encodeHalfTwists(twistsArr),
      shape: shape || null,
      prepSkill,
      startPosition: startPosition || null,
      landing: landing || null,
      direction: direction || null,
      difficulty: difficulty === '' ? null : Number(difficulty),
      // A typed DMT value is kept; otherwise the server works it out.
      dmtDifficulty: dmtOverridden && dmtDifficulty !== '' ? Number(dmtDifficulty) : null,
      figNotation: normalizeFig(figNotation) || null,
    };
    if (showOrder) payload.order = parseInt(order) || 1;
    onSave(payload);
  };

  const calcNote = (overridden, onReset, calculated) => (overridden
    ? <button type="button" className="bk-btn bk-btn--link sf-reset" onClick={onReset}>Use calculated ({calculated})</button>
    : <span className="sf-hint">Calculated</span>);

  return (
    <div className="modal-overlay">
      <div className="modal sf-modal">
        <div className="modal-header">
          <h3>{isEdit ? 'Edit skill' : 'New skill'}</h3>
          <button type="button" onClick={onCancel} className="close-button" aria-label="Close">×</button>
        </div>
        <form onSubmit={handleSubmit} className="sf-form">
          <label className="sf-field">
            <span className="sf-label">Name</span>
            <input className="bk-input" type="text" value={name} onChange={(e) => setName(e.target.value)} required />
          </label>

          <label className="sf-field">
            <span className="sf-label">Description</span>
            <textarea className="bk-input" value={description} onChange={(e) => setDescription(e.target.value)} rows="2" />
          </label>

          <label className="sf-check">
            <input type="checkbox" className="bk-checkbox" checked={prepSkill}
              onChange={(e) => { setPrepSkill(e.target.checked); structureChanged(); }} />
            <span>Prep skill: no tariff, and can't go in a routine</span>
          </label>

          <fieldset className="sf-section">
            <legend>Shape</legend>
            <div className="sf-grid">
              <label className="sf-field">
                <span className="sf-label">¼ somersaults</span>
                <input className="bk-input" type="number" inputMode="numeric" value={quarterSoms === 0 ? '' : quarterSoms} min="0" max="16" placeholder="0"
                  onChange={(e) => { setQuarterSoms(parseInt(e.target.value) || 0); structureChanged(); }} />
              </label>
              <label className="sf-field">
                <span className="sf-label">Direction</span>
                <select className="bk-input" value={direction || ''} onChange={e => { setDirection(e.target.value); structureChanged(); }}>
                  <option value="">None</option>
                  <option value="backward">Backward</option>
                  <option value="forward">Forward</option>
                  <option value="sideways">Sideways</option>
                </select>
              </label>
              <label className="sf-field">
                <span className="sf-label">Shape</span>
                <select className="bk-input" value={shape} onChange={e => { setShape(e.target.value); structureChanged(); }}>
                  <option value="">None</option>
                  <option value="tuck">Tuck</option>
                  <option value="pike">Pike</option>
                  <option value="straight">Straight</option>
                  <option value="straddle">Straddle</option>
                </select>
              </label>
              <label className="sf-field">
                <span className="sf-label">Starts on</span>
                <select className="bk-input" value={startPosition} onChange={e => setStartPosition(e.target.value)}>
                  {!startPosition && <option value="">Not set</option>}
                  <option value="feet">Feet</option>
                  <option value="seat">Seat</option>
                  <option value="front">Front</option>
                  <option value="back">Back</option>
                </select>
              </label>
              <label className="sf-field">
                <span className="sf-label">Lands on</span>
                <select className="bk-input" value={landing} onChange={e => { setLanding(e.target.value); structureChanged(); }}>
                  <option value="feet">Feet</option>
                  <option value="seat">Seat</option>
                  <option value="front">Front</option>
                  <option value="back">Back</option>
                </select>
              </label>
            </div>
            <div className="sf-field">
              <span className="sf-label">{quarterSoms <= 4 ? '½ twists' : '½ twists in each somersault'}</span>
              <div className="sf-twists">
                {twistsArr.map((t, i) => (
                  <input
                    key={i}
                    className="bk-input"
                    type="number"
                    inputMode="numeric"
                    min="0"
                    max="9"
                    placeholder="0"
                    aria-label={`½ twists in somersault ${i + 1}`}
                    value={t === 0 ? '' : t}
                    onChange={(e) => {
                      const v = Math.max(0, Math.min(9, parseInt(e.target.value) || 0));
                      setTwistsArr(arr => arr.map((x, j) => j === i ? v : x));
                      structureChanged();
                    }}
                  />
                ))}
              </div>
            </div>
            <details className="sf-breakdown">
              <summary>How the trampoline difficulty is worked out</summary>
              {calc.breakdown.map((b, i) => (
                <div key={i} className="sf-breakdown__row">
                  <span>{b.label}</span>
                  <span>{b.points >= 0 ? '+' : ''}{b.points.toFixed(1)}</span>
                </div>
              ))}
              <div className="sf-breakdown__row sf-breakdown__total">
                <span>Total</span>
                <span>{calc.difficulty.toFixed(1)}</span>
              </div>
            </details>
            <details className="sf-breakdown">
              <summary>How the double mini difficulty is worked out</summary>
              {dmtWorking.value == null ? (
                <p className="sf-breakdown__note">{dmtWorking.reason}</p>
              ) : (
                <>
                  {dmtWorking.steps.map((step, i) => (
                    <div key={i} className="sf-breakdown__row">
                      <span>{step.label}</span>
                      <span>{step.amount}</span>
                    </div>
                  ))}
                  <div className="sf-breakdown__row sf-breakdown__total">
                    <span>Total</span>
                    <span>{dmtWorking.value.toFixed(1)}</span>
                  </div>
                </>
              )}
            </details>
          </fieldset>

          <fieldset className="sf-section">
            <legend>Difficulty and FIG</legend>
            <div className="sf-grid">
              <label className="sf-field">
                <span className="sf-label">Trampoline</span>
                <input className="bk-input" type="number" inputMode="decimal" step="0.1" min="0" value={difficulty}
                  onChange={e => { setDifficulty(e.target.value); setDiffOverridden(true); }} />
                {calcNote(diffOverridden, () => { setDifficulty(String(calc.difficulty)); setDiffOverridden(false); }, calc.difficulty.toFixed(1))}
              </label>
              <label className="sf-field">
                <span className="sf-label">Double mini</span>
                <input className="bk-input" type="number" inputMode="decimal" step="0.1" min="0" value={dmtDifficulty}
                  placeholder="None"
                  onChange={e => { setDmtDifficulty(e.target.value); setDmtOverridden(true); }} />
                {dmtOverridden
                  ? calcNote(true, () => { setDmtOverridden(false); setDmtDifficulty(calcDmt == null ? '' : String(calcDmt)); }, calcDmt == null ? 'none' : calcDmt.toFixed(1))
                  : <span className="sf-hint">{calcDmt == null ? 'None: not feet to feet' : 'Calculated'}</span>}
              </label>
              <label className="sf-field">
                <span className="sf-label">FIG notation</span>
                <input className="bk-input" type="text" value={figNotation} style={{ fontFamily: 'monospace' }}
                  onChange={e => { setFigNotation(e.target.value); setFigOverridden(true); }} />
                {calcNote(figOverridden, () => { setFigNotation(calc.figNotation); setFigOverridden(false); }, calc.figNotation || 'blank')}
              </label>
              {showOrder && (
                <label className="sf-field">
                  <span className="sf-label">Order</span>
                  <input className="bk-input" type="number" inputMode="numeric" value={order} onChange={(e) => setOrder(e.target.value)} min="1" />
                </label>
              )}
            </div>
          </fieldset>

          <div className="sf-actions">
            {extraActions && <div className="sf-actions__extra">{extraActions}</div>}
            <button type="button" onClick={onCancel} className="bk-btn bk-btn--outline">Cancel</button>
            <button type="submit" className="bk-btn bk-btn--primary">{isEdit ? 'Save' : 'Add skill'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
